import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	ActivationStart,
	NavigationEnd,
	Router,
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import {
	ComponentController,
	MessagingService,
	filterNil,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { locale as momentLocale } from 'moment';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest, forkJoin, interval, of } from 'rxjs';
import {
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	map,
	mapTo,
	pluck,
	shareReplay,
	startWith,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';

import { CommandEvent } from 'application/common/model/command-event';
import { FirstCoachingPlanModuleDialogComponent } from 'dialog/first-coaching-plan-module-dialog/first-coaching-plan-module-dialog.component';
import { FirstCoachingPlanModuleDialogData } from 'dialog/first-coaching-plan-module-dialog/first-coaching-plan-module-dialog.data';
import { SetModuleCommentDialogData } from 'dialog/set-module-comment-dialog/set-module-comment-dialog.data';
import { SetModuleRatingDialogComponent } from 'dialog/set-module-rating-dialog/set-module-rating-dialog.component';
import { environment } from 'environments/environment';
import { ModuleType } from 'module/model/module.type';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { IOrganization } from 'organization/organization';
import { AppController } from 'presentation/app.controller';
import { UserMoodApplicationPort } from 'user/port/mood/user-mood-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';
import { IUserEmailModel } from 'user/user-email-model';

import { filterError } from '../app.util';
import { AppState, AppStore } from './app.store';
import { AuthGuard } from './common/guard/auth.guard';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AppService extends ComponentController<AppState> {
	public appChatUserId$: Observable<string>;
	public appChatWidthPx$: Observable<number>;
	public appSlideWidthPx$: Observable<number>;
	public appToolbarHeight$: Observable<number>;
	public lastInteraction$: Observable<number>;
	public isSmallScreen$: Observable<boolean>;
	public focusModeEnabled$: Observable<boolean>;
	public currentLang$: Observable<string>;
	public appSidebarSelectedUserId$: Observable<string>;
	public canShowProductTour$: Observable<boolean>;
	public isFirstModuleDialogOpen$: Observable<boolean>;

	private _actionsStatus$: Observable<{
		mustAnswerFirstCoachingPlanModule: boolean;
		mustAnswerOnBoardingModule: boolean;
		mustAskMood: boolean;
	}>;
	private _user$: Observable<IUser>;
	private _organization$: Observable<IOrganization>;

	constructor(
		store: AppStore,
		private _router: Router,
		private _swUpdates: SwUpdate,
		private _routerQuery: RouterQuery,
		private _appController: AppController,
		private _dialogService: DialogService,
		private _messageService: MessageService,
		private _translateService: TranslateService,
		@Inject(DOCUMENT) private _document: Document,
		private _appMessagingService: MessagingService,
		private _breakPointObserver: BreakpointObserver,
		private _userApplicationPort: UserApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort,
		private _userMoodApplicationPort: UserMoodApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public updateShowFocusMode(showFocusMode: boolean) {
		return this.store.update({ showFocusMode });
	}

	public sendContactEmail(emailAddress: string, model?: IUserEmailModel) {
		const top = (window.screen.height - 500) / 2;
		const left = (window.screen.width - 700) / 2;

		this._document.open(
			`mailto:${emailAddress}?body=${encodeURIComponent(
				model?.body ?? ''
			)}&subject=${model?.subject ?? ''}`,
			'email',
			`width=700,height=500,toolbar=false,status=false,left=${left},top=${top}`
		);
	}

	public setAppChatUserId(userId: string) {
		this.store.update({ appChatUserId: userId });
	}

	public toggleChat() {
		this.store.update((state) => ({ chatOpened: !state.chatOpened }));
	}

	public showChat() {
		this.store.update({ chatOpened: true });
	}

	public toggleSideBar() {
		this.store.update((state) => ({ sideBarOpened: !state.sideBarOpened }));
	}

	public updateLastInteraction() {
		this.store.update({ lastInteraction: Date.now() });
	}

	public updateFocusMode(focusModeEnabled: boolean) {
		this.store.update({ focusModeEnabled });
	}

	public setAppSidebarSelectedUserId(userId: string) {
		this.store.update({ appSidebarSelectedUserId: userId });
	}

	public showSideBar() {
		this.store.update(() => ({ sideBarOpened: true }));
	}

	private _initListeners() {
		const onBoardingModuleId$ = this._organization$.pipe(
			switchMap((organization) =>
				organization
					? this._moduleApplicationPort.fetchOnBoardingModuleId(organization.id)
					: of(null)
			)
		);

		const firstCoachingPlanModuleId$ = this._moduleApplicationPort
			.fetchFirstCoachingPlanModule()
			.pipe(filterError);

		const user$ = this._user$.pipe(
			filterNil(),
			distinctUntilKeyChanged('id'),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		combineLatest([
			this._actionsStatus$,
			firstCoachingPlanModuleId$,
			onBoardingModuleId$,
			user$,
		])
			.pipe(
				tap(
					([
						{
							mustAnswerFirstCoachingPlanModule,
							mustAnswerOnBoardingModule,
							mustAskMood,
						},
						firstCoachingPlanModuleId,
						onBoardingModuleId,
						user,
					]) => {
						if (!user.isCoachee) {
							return;
						}

						if (mustAnswerOnBoardingModule && onBoardingModuleId) {
							this._router.navigate([
								'/activity',
								onBoardingModuleId,
								'user',
								user.id,
							]);
							return;
						}

						if (user.metadata?.language)
							this._translateService.setDefaultLang(user.metadata?.language);

						if (mustAnswerFirstCoachingPlanModule && firstCoachingPlanModuleId) {
							this._dialogService.open(FirstCoachingPlanModuleDialogComponent, {
								header: this._translateService.instant(
									this._appController.i18n + '.firstCoachingPlanModuleDialog.header'
								),
								data: {
									coachingPlanModuleId: firstCoachingPlanModuleId,
									userId: user.id,
								} as FirstCoachingPlanModuleDialogData,
								dismissableMask: false,
								closeOnEscape: false,
								closable: false,
								width: '700px',
							});
							this.store.update({ isFirstModuleDialogOpen: true });
							return;
						}

						if (mustAskMood) {
							this._appController.openMoodDialog();
							return;
						}
					}
				),
				untilDestroyed(this)
			)
			.subscribe();

		this._router.events
			.pipe(
				tap(
					(event) =>
						event instanceof NavigationEnd && (window as any).Intercom('update')
				),
				untilDestroyed(this)
			)
			.subscribe();

		this._userApplicationPort
			.observeUserModuleSuggestionsChanges()
			.pipe(
				tap((e) =>
					this._dialogService.open(SetModuleRatingDialogComponent, {
						header: this._translateService.instant(
							'presentation.app.common.dialog.userModuleRating.title'
						),
						data: {
							moduleId: e.data,
							coacheeId: e.entityId,
						} as SetModuleCommentDialogData,
						dismissableMask: true,
						width: '700px',
					})
				),
				untilDestroyed(this)
			)
			.subscribe();

		const urlTargetUser$ = this._routerQuery.selectParams('userId').pipe(
			switchMap((userId) =>
				userId
					? this._userApplicationPort.observeUserById(userId).pipe(pluck('data'))
					: of(null as IUser)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		combineLatest([
			this._router.events.pipe(
				filter((e) => e instanceof ActivationStart),
				map((e) => e as ActivationStart),
				map((e) => ({
					isProtected: (e.snapshot.routeConfig.canActivate || []).some(
						(guard) => guard === AuthGuard
					),
					route: e.snapshot,
				}))
			),
			this._organization$,
			this._user$,
		])
			.pipe(
				tap(([{ isProtected, route }, organization, user]) =>
					this._redirectIfNeeded(user, organization, isProtected, route)
				),
				untilDestroyed(this)
			)
			.subscribe();

		this._user$
			.pipe(
				filterNil(),
				take(1),
				tap(
					(user) =>
						!user.metadata.language &&
						this._userApplicationPort.updateUserLanguage(
							this._translateService.currentLang
						)
				),
				tap(
					(user) =>
						user.metadata.language &&
						this._translateService.use(user.metadata.language)
				),
				filter((user) => user.isCoachee),
				untilDestroyed(this)
			)
			.subscribe();

		combineLatest([this._user$, urlTargetUser$])
			.pipe(
				map(([user, urlTargetUser]) => user || urlTargetUser),
				filterNil(),
				switchMap((user) => user.organization$),
				distinctUntilChanged(
					(a, b) => a?.id === b?.id && a?.iconUrl === b?.iconUrl
				),
				tap((organization) => {
					(document.getElementById('theme') as HTMLLinkElement).href =
						organization?.id
							? environment.styleUrl.replace('{id}', organization.id)
							: environment.styleUrl.replace('{id}/', '');
					(document.getElementById('ico') as HTMLLinkElement).href =
						organization?.iconUrl ?? '/assets/images/format_carré_couleurs.png';
				}),
				untilDestroyed(this)
			)
			.subscribe();

		this._appMessagingService
			.observeMessage()
			.pipe(
				tap((message) =>
					this._showMessage(
						message.severity,
						message.summaryKey,
						message.detailKey,
						message.params
					)
				),
				untilDestroyed(this)
			)
			.subscribe();

		this._translateService.onLangChange
			.pipe(
				tap((e) => momentLocale(e.lang)),
				untilDestroyed(this)
			)
			.subscribe();

		interval(60 * 60 * 1000)
			.pipe(
				tap(() => this._swUpdates.checkForUpdate()),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this._user$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ refCount: true, bufferSize: 1 }));

		this._organization$ = this._user$.pipe(
			switchMap((user) => user?.organization$ ?? of(null)),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this._actionsStatus$ = combineLatest([
			this._userApplicationPort.commandHandled$.pipe(
				filter((e) => e.method === 'saveUserAnswers'),
				startWith(null as CommandEvent<unknown>)
			),
			this._user$.pipe(
				filterNil(),
				distinctUntilKeyChanged('id'),
				filter((user) => user.isCoachee)
			),
		]).pipe(
			switchMap(() =>
				combineLatest([
					this._userApplicationPort.mustAnswerFirstCoachingPlanModule(),
					this._userApplicationPort.mustAnswerOnBoardingModule(),
					this._userMoodApplicationPort.mustAskMood(),
				])
			),
			map(
				([
					mustAnswerFirstCoachingPlanModule,
					mustAnswerOnBoardingModule,
					mustAskMood,
				]) => ({
					mustAnswerFirstCoachingPlanModule,
					mustAnswerOnBoardingModule,
					mustAskMood,
				})
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.focusModeEnabled$ = this.select('focusModeEnabled').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.canShowProductTour$ = this._actionsStatus$.pipe(
			map(
				({
					mustAnswerFirstCoachingPlanModule,
					mustAnswerOnBoardingModule,
					mustAskMood,
				}) =>
					!mustAnswerFirstCoachingPlanModule &&
					!mustAnswerOnBoardingModule &&
					!mustAskMood
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.currentLang$ = this._translateService.onLangChange.pipe(
			map((e) => e.lang),
			startWith(this._translateService.currentLang),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSmallScreen$ = this._breakPointObserver
			.observe([Breakpoints.XSmall, Breakpoints.Small])
			.pipe(
				map((state) => state.matches),
				shareReplay({ bufferSize: 1, refCount: true })
			);

		this.appToolbarHeight$ = this.select('appToolbarHeight').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appSlideWidthPx$ = this.select('appSlideWidthPx').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appChatWidthPx$ = this.select('appChatWidthPx').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.lastInteraction$ = this.select('lastInteraction').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appChatUserId$ = this.select('appChatUserId').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appSidebarSelectedUserId$ = this.select('appSidebarSelectedUserId').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.isFirstModuleDialogOpen$ = this.select('isFirstModuleDialogOpen').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	private _redirectIfNeeded(
		user: IUser,
		organization: IOrganization,
		isProtected: boolean,
		route: ActivatedRouteSnapshot
	) {
		if (!environment.production) {
			return;
		}

		if (!user && isProtected) {
			this.store.update({ redirecting: true });
			this.store.setLoading(true);

			document.location.href = environment.authWebsiteUrl;
		}

		if (user?.isAdmin || user?.isManager) {
			document.location.href = environment.clientWebsiteUrl;
			this.store.update({ redirecting: true });
			this.store.setLoading(true);
			return;
		}

		if (
			user?.isCoach &&
			!route.pathFromRoot.some((r) => r.url.some((seg) => seg.path === 'activity'))
		) {
			document.location.href = environment.clientWebsiteUrl;
			this.store.update({ redirecting: true });
			this.store.setLoading(true);
			return;
		}
	}

	private _showMessage(
		severity: 'success' | 'error' | 'info',
		summaryKey: string,
		detailKey: string,
		params: { [key: string]: any }
	): Promise<void> {
		return forkJoin([
			this._translateService.get(detailKey, params),
			this._translateService.get(summaryKey, params),
		])
			.pipe(
				tap(([detail, summary]) =>
					this._messageService.add({ detail, severity, summary, life: 5000 })
				),
				mapTo(null)
			)
			.toPromise();
	}

	public markFirstModuleDialogAsClosed(): void {
		this.store.update({ isFirstModuleDialogOpen: false });
	}
}
